import moment from "moment";

import {
  DepreciationTransactionRow,
  ITransaction,
  KontaxTransactionFilterOptions,
} from "../../../../types";
import { SAVE_DATE_FORMAT } from "@/v1/constants";

export const isDepreciationRow = (
  transaction: Pick<ITransaction, "isDepreciationRow">
): boolean => transaction.isDepreciationRow === true;

export const mapDepreciationRowsToTransactions = ({
  rows,
  email,
}: {
  rows: DepreciationTransactionRow[];
  email: string;
}): ITransaction[] =>
  rows.map((row) => ({
    id: row.id,
    isDepreciationRow: true,
    email,
    firstName: "",
    lastName: "",
    name: "",
    iban: "",
    amount: row.amount,
    currency: "EUR",
    valutaDate: row.valutaDate,
    description: row.description,
    assets: [],
    verified: undefined,
    categoryCode: null,
    vatCategoryCode: null,
    submittedCategoryCode: null,
    submittedVatCategoryCode: null,
    submissionStatus: null,
    assetTypeLabel: row.assetTypeLabel,
  }));

/*
 * An open lower bound still needs a floor. Business assets cannot predate the
 * company, so anything before this is noise, and starting the loop at year 0
 * would iterate two thousand times to no purpose.
 */
const MIN_DEPRECIATION_YEAR = 1900;
const MIN_DEPRECIATION_YEAR_START = `${MIN_DEPRECIATION_YEAR}-01-01`;

export const getYearsWithYearEndInDateFilter = ({
  dateFrom,
  dateTo,
}: Pick<KontaxTransactionFilterOptions, "dateFrom" | "dateTo">): number[] => {
  if (!dateFrom && !dateTo) {
    return [];
  }

  // The filters are plain YYYY-MM-DD dates, which compare correctly as strings.
  // Parsing them into moments would apply the browser's timezone and shift the
  // boundary for operators outside Berlin.
  const from = dateFrom || MIN_DEPRECIATION_YEAR_START;
  const to = dateTo || moment().format(SAVE_DATE_FORMAT);
  const years: number[] = [];

  /*
   * Pad the year, because these are string comparisons: an unpadded "999-12-31"
   * sorts above "2026-08-24" and the year would be dropped. An open lower bound
   * also has to start somewhere sane rather than at year 0.
   */
  for (
    let year = Math.max(Number(from.slice(0, 4)), MIN_DEPRECIATION_YEAR);
    year <= Number(to.slice(0, 4));
    year++
  ) {
    const yearEnd = `${String(year).padStart(4, "0")}-12-31`;
    if (yearEnd >= from && yearEnd <= to) {
      years.push(year);
    }
  }

  return years;
};

export const canFetchDepreciationRowsForFilters = ({
  direction,
  source,
  categories,
  vatCategoryCodes,
  search,
  excludeInternal,
  verified,
  verifiedByML,
  submissionStatus,
  assets,
  internalNote,
  personalNote,
}: KontaxTransactionFilterOptions): boolean =>
  direction === undefined &&
  source === undefined &&
  !categories?.length &&
  !vatCategoryCodes?.length &&
  !search?.trim() &&
  excludeInternal !== false &&
  verified === undefined &&
  verifiedByML === undefined &&
  !submissionStatus &&
  assets === undefined &&
  internalNote == null &&
  personalNote == null;

const isDateSort = (
  sort: KontaxTransactionFilterOptions["sort"] | undefined
): boolean => !sort || ["valutaDate", "valuta_date"].includes(sort.field);

export const shouldFetchDepreciationRowsForPage = ({
  page,
  pageSize,
  totalCount,
  sort,
}: {
  page: number;
  pageSize: number;
  totalCount: number;
  sort?: KontaxTransactionFilterOptions["sort"];
}): boolean => {
  if (!isDateSort(sort)) {
    return false;
  }

  const lastPage = Math.max(1, Math.ceil(totalCount / pageSize));
  if (sort?.direction?.toUpperCase() === "ASC") {
    return page === lastPage;
  }

  return page === 1;
};

const shouldInjectDepreciationRows = ({
  depreciationRows,
  sort,
}: {
  depreciationRows: ITransaction[];
  sort?: KontaxTransactionFilterOptions["sort"];
}): boolean => depreciationRows.length > 0 && isDateSort(sort);

export const mergeDepreciationRowsIntoTransactions = ({
  transactions,
  depreciationRows,
  sort,
}: {
  transactions: ITransaction[];
  depreciationRows: ITransaction[];
  sort?: KontaxTransactionFilterOptions["sort"];
}): ITransaction[] => {
  if (
    !shouldInjectDepreciationRows({
      depreciationRows,
      sort,
    })
  ) {
    return transactions;
  }

  const direction = sort?.direction?.toUpperCase() === "DESC" ? -1 : 1;

  // Both sides carry plain YYYY-MM-DD dates, so compare them as strings. That
  // keeps the interleaving stable no matter which endpoint a row came from.
  return [...transactions, ...depreciationRows].sort((a, b) => {
    const dateDiff = a.valutaDate.localeCompare(b.valutaDate);
    if (dateDiff !== 0) {
      return dateDiff * direction;
    }
    return a.id.localeCompare(b.id) * direction;
  });
};
